import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import {
  ClinicalRequest, ClinicalRequestOpenCount, ClinicalRequestStatus,
} from '../../domain/models/clinical-request.model';
import { IClinicalRequestsRepository } from '../../domain/repositories/clinical-requests.repository';
import { ClinicalRequestsRemoteDatasource } from '../datasources/remote/clinical-requests.remote-datasource';
import { ClinicalRequestMapper } from '../mappers/clinical-request.mapper';

// The status filter travels in the PascalCase the backend stores/compares (`"Pending"`), not in the
// lowercase domain vocabulary -- the mapper owns that translation in both directions.
const WIRE_STATUS: Record<ClinicalRequestStatus, string> = {
  pending: 'Pending',
  completed: 'Completed',
  rejected: 'Rejected',
};

@Injectable()
export class ClinicalRequestsRepositoryImpl implements IClinicalRequestsRepository {
  private readonly datasource = inject(ClinicalRequestsRemoteDatasource);
  private readonly mapper = inject(ClinicalRequestMapper);

  public list(status?: ClinicalRequestStatus, departmentId?: number): Observable<readonly ClinicalRequest[]> {
    return this.datasource.list(status ? WIRE_STATUS[status] : undefined, departmentId).pipe(
      map((dto) => dto.Items.map((item) => this.mapper.toClinicalRequest(item))),
    );
  }

  public listByIds(ids: readonly string[]): Observable<readonly ClinicalRequest[]> {
    return this.datasource.listByIds(ids).pipe(
      map((dto) => dto.Items.map((item) => this.mapper.toClinicalRequest(item))),
    );
  }

  public openCount(departmentId?: number): Observable<ClinicalRequestOpenCount> {
    return this.datasource.openCount(departmentId).pipe(map((dto) => this.mapper.toOpenCount(dto)));
  }

  public complete(id: string): Observable<ClinicalRequest> {
    return this.datasource.updateStatus(id, this.mapper.toStatusUpdatePayload('completed')).pipe(
      map((dto) => this.mapper.toClinicalRequest(dto)),
    );
  }

  public reject(id: string, note: string): Observable<ClinicalRequest> {
    return this.datasource.updateStatus(id, this.mapper.toStatusUpdatePayload('rejected', note)).pipe(
      map((dto) => this.mapper.toClinicalRequest(dto)),
    );
  }
}
